'use client';

import { useMemo, useState } from 'react';

import { InvoiceForCurrencyForLegalEntity } from '@ravago/shared/page-data/models/elements';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { DatePicker } from '@ravago/shared/radiance/components/input-field/components/DatePicker/DatePicker';
import { InputGroup } from '@ravago/shared/radiance/components/input-field/components/InputGroup/InputGroup';
import { Multiselect, MultiselectOption } from '@ravago/shared/radiance/components/input-field/components/Multiselect/Multiselect';
import { Switch } from '@ravago/shared/radiance/components/switch/components/Switch/Switch';
import { useTranslations } from '@ravago/shared/translations/client';

import { OpenInvoiceFilters as OpenInvoiceFiltersModel } from '../../../../models/filters.model';
import { InvoiceFilterUtils } from '../../../../utils/invoice-filter.utils';

interface Props {
   openInvoices: InvoiceForCurrencyForLegalEntity[];
   filters: OpenInvoiceFiltersModel;
   onFiltersChange: (filters: OpenInvoiceFiltersModel) => void;
}

export const OpenInvoiceFilters = ({ openInvoices, filters, onFiltersChange }: Props) => {
   const translate = useTranslations();
   const [showFilters, setShowFilters] = useState(true);

   const filteredOpenInvoices = useMemo(() => InvoiceFilterUtils.filterOpenInvoices(openInvoices, filters, true), [openInvoices, filters]);

   const updatedTypes = useMemo(() => {
      if (openInvoices.length === 0) return filters.documentTypes;

      const types = InvoiceFilterUtils.getDocumentTypeOptions(openInvoices)
         .map((type) => ({
            ...type,
            label: translate(type.label)
         }))
         .sort((a, b) => a.label.localeCompare(b.label));

      const allInvoiceItems =
         filteredOpenInvoices?.flatMap((invoice) => invoice.currencyTransactions.flatMap((currencyInvoice) => currencyInvoice.transactions)) || [];

      const typeCounts = allInvoiceItems.reduce((acc, invoiceItem) => {
         acc[invoiceItem.type] = (acc[invoiceItem.type] || 0) + 1;
         return acc;
      }, {} as Record<string, number>);

      return types.map((type) => {
         const isChecked = filters.documentTypes?.find((dt) => dt.id === type.id)?.checked || false;
         const count = typeCounts[type.id] || 0;
         const isTypePresent = count > 0;

         return {
            ...type,
            checked: isChecked,
            disabled: !isTypePresent,
            trailingText: `(${count})`
         };
      });
   }, [openInvoices, filters]);

   const isOverdueFilterDisabled = useMemo(() => !InvoiceFilterUtils.hasOverdueInvoices(filteredOpenInvoices), [filteredOpenInvoices]);

   const activeFilterCount = useMemo(() => {
      let amount = 0;

      if (filters.overdue) amount += 1;
      if (filters.dueDate !== '') amount += 1;
      if (filters.documentTypes.some((document) => document.checked)) amount += 1;
      return amount;
   }, [filters.documentTypes, filters.dueDate, filters.overdue]);

   const handleClearFilters = () => {
      onFiltersChange({
         ...filters,
         dueDate: '',
         documentTypes: [],
         overdue: false
      });
   };

   const handleClearSearch = () => {
      onFiltersChange({
         ...filters,
         eInvoiceId: ''
      });
   };

   const handleInvoiceSearch = (inputValue: string) => {
      if (inputValue.trim() === '') {
         handleClearSearch();
         return;
      }

      onFiltersChange({
         ...filters,
         eInvoiceId: inputValue
      });
   };

   const handleDueDateFilter = (date: string) => {
      onFiltersChange({
         ...filters,
         dueDate: date
      });
   };

   const handleDocumentTypeFilter = (options: MultiselectOption[]) => {
      onFiltersChange({
         ...filters,
         documentTypes: options
      });
   };

   const resetDocumentTypeFilter = () => {
      const types = filters.documentTypes.map((type) => {
         type.checked = false;
         return type;
      });
      onFiltersChange({
         ...filters,
         documentTypes: types
      });
   };

   const handleOverdueFilter = (checked: boolean) => {
      onFiltersChange({
         ...filters,
         overdue: checked
      });
   };

   const renderFilters = (isDesktop = true) => (
      <Box direction={isDesktop ? 'row' : 'column'} width="100%" gap={isDesktop ? 'lg' : 'md'}>
         <Box width={isDesktop ? '25%' : '100%'}>
            <DatePicker
               id="open-invoices-due-by-filter"
               label={translate('invoices.filters.invoices-due-by')}
               min={new Date().toISOString().split('T')[0]}
               value={filters.dueDate}
               onChange={handleDueDateFilter}
            />
         </Box>
         <Multiselect
            clearText={translate('invoices.filters.clear')}
            id="open-invoices-document-type-filter"
            label={translate('invoices.filters.document-type')}
            options={updatedTypes}
            onChange={handleDocumentTypeFilter}
            onClear={resetDocumentTypeFilter}
            sortOptionsOnOpen={false}
         />
         <Switch
            id="open-invoices-overdue-filter"
            label={translate('invoices.filters.show-only-overdue')}
            disabled={isOverdueFilterDisabled}
            checked={filters.overdue}
            onChange={handleOverdueFilter}
         />
         <Box>
            <Button type="button" variant="secondary" onClick={handleClearFilters}>
               {translate('invoices.filters.reset-filters')}
            </Button>
         </Box>
      </Box>
   );

   return (
      <Box direction="column" width="100%">
         <Box>
            <InputGroup
               actionIcon="search"
               autocomplete="on"
               clearable
               id="open-invoices-search"
               label={translate('invoices.filters.search-invoice')}
               maxLength={255}
               onActionClick={handleInvoiceSearch}
               onEnter={handleInvoiceSearch}
               onClear={handleClearSearch}
               defaultValue={filters.eInvoiceId ?? ''}
            />
            <BreakpointSwitch includeViews={['desktop']}>
               <Button leadingIcon="sliders" variant="tertiary" onClick={() => setShowFilters(!showFilters)}>
                  <Box gap="sm">
                     {showFilters ? translate('invoices.filters.hide-filters') : translate('invoices.filters.show-filters')}
                     <span>{!showFilters && activeFilterCount > 0 && <Badge variant="primary">{activeFilterCount}</Badge>}</span>
                  </Box>
               </Button>
            </BreakpointSwitch>
            <BreakpointSwitch excludeViews={['desktop']}>
               <Box gap="none">
                  <IconButton onClick={() => setShowFilters(!showFilters)} variant="on-light" backdrop icon="sliders" />
                  <Box position="relative" left={-15} margin="none" spacing="none" gap="none">
                     {!showFilters && activeFilterCount > 0 && <Badge variant="primary">{activeFilterCount}</Badge>}
                  </Box>
               </Box>
            </BreakpointSwitch>
         </Box>
         {showFilters && (
            <>
               <BreakpointSwitch includeViews={['desktop']}>{renderFilters()}</BreakpointSwitch>
               <BreakpointSwitch excludeViews={['desktop']}>{renderFilters(false)}</BreakpointSwitch>
            </>
         )}
      </Box>
   );
};
